import type { TodayCard, TodayRow } from './today';

/**
 * The morning screen as one work list (owner, 2026-10-23: option A of "Today screen options").
 *
 * <p>
 * The cards' own ids, put in the order a counter works them — what is late first, then what goes
 * out, then what comes back, then the workroom — each with the one step its rows lead to. The
 * screen renders a section only while it has rows; the counts strip above says how many of each.
 * </p>
 */

/** The order the day is worked in, by the morning cards' ids. */
export const workOrder = [
  'overdue',
  'pickups',
  'returns',
  'inspection',
  'alterations',
  'cleaning',
] as const;

export type WorkSectionId = (typeof workOrder)[number];

/** What a section is called on the list, and the step a row's button takes. */
export const workSections: Record<
  WorkSectionId,
  { heading: string; action: string; group: 'late' | 'counter' | 'workroom' }
> = {
  overdue: { heading: 'Late', action: 'Take back', group: 'late' },
  pickups: { heading: 'Hand over today', action: 'Hand over', group: 'counter' },
  returns: { heading: 'Take back today', action: 'Take back', group: 'counter' },
  inspection: { heading: 'To check', action: 'Inspect', group: 'workroom' },
  alterations: { heading: 'Back from the tailor', action: 'Open', group: 'workroom' },
  cleaning: { heading: 'Back from the cleaner', action: 'Open', group: 'workroom' },
};

/** A booking row's button opens that booking; a workroom row's opens its queue. */
export function opensBooking(id: WorkSectionId): boolean {
  return id === 'overdue' || id === 'pickups' || id === 'returns';
}

/** The sections this person may see, in working order, each with its rows — the empty left out. */
export function workList<T extends { rows: readonly TodayRow[]; failed: boolean }>(
  cards: readonly TodayCard[],
  dataFor: (id: string) => T,
): { card: TodayCard; id: WorkSectionId; data: T }[] {
  return workOrder.flatMap((id) => {
    const card = cards.find((candidate) => candidate.id === id);
    const data = card ? dataFor(id) : undefined;

    return card && data && !data.failed && data.rows.length > 0 ? [{ card, id, data }] : [];
  });
}

/** One chip of the counts strip: how many, said in words, and whether it needs attention. */
export interface CountChip {
  id: 'pickups' | 'returns' | 'overdue' | 'workroom';
  count: number;
  label: string;
  attention: boolean;
}

/**
 * The four counts above the list — to hand over, to take back, late, in the workroom — for the
 * cards this person can see. The workroom is one figure for checking, tailor and cleaner together:
 * on the list they are rows, and three more chips would be the band of six this replaced.
 */
export function countChips(
  visible: (id: string) => boolean,
  count: (id: string) => number | undefined,
): CountChip[] {
  const chips: CountChip[] = [];
  const add = (id: CountChip['id'], n: number, one: string, many: string, attention = false) =>
    chips.push({ id, count: n, label: n === 1 ? one : many, attention: attention && n > 0 });

  if (visible('pickups')) {
    add('pickups', count('pickups') ?? 0, 'to hand over', 'to hand over');
  }

  if (visible('returns')) {
    add('returns', count('returns') ?? 0, 'to take back', 'to take back');
  }

  if (visible('overdue')) {
    add('overdue', count('overdue') ?? 0, 'late return', 'late returns', true);
  }

  const workroom = (['inspection', 'alterations', 'cleaning'] as const).filter(visible);

  if (workroom.length > 0) {
    add(
      'workroom',
      workroom.reduce((sum, id) => sum + (count(id) ?? 0), 0),
      'in the workroom',
      'in the workroom',
    );
  }

  return chips;
}

/**
 * Tomorrow in a sentence — "2 garments go out · 1 comes back" — and how many going out are still
 * flagged (at the tailor), which is the one thing to chase today.
 */
export function tomorrowSummary(
  out: readonly TodayRow[],
  back: readonly TodayRow[],
): { line: string; chase: number } {
  const garments = (n: number) => `${n} garment${n === 1 ? '' : 's'}`;
  const parts = [
    out.length > 0 ? `${garments(out.length)} ${out.length === 1 ? 'goes' : 'go'} out` : null,
    back.length > 0
      ? `${garments(back.length)} ${back.length === 1 ? 'comes' : 'come'} back`
      : null,
  ].filter((part): part is string => part !== null);

  return {
    line: parts.length > 0 ? parts.join(' · ') : 'Nothing goes out or comes back tomorrow.',
    chase: out.filter((row) => row.flagged).length,
  };
}
